const BP601TSynthesisNotes = `
<div class="space-y-8 animate-fadeIn">
    
    <!-- 1. Chapter Header -->
    <div>
        <h2 class="text-2xl font-black text-indigo-900 mb-3 border-b-2 border-slate-200 pb-2">Complete Synthesis of Important Drugs</h2>
        <p class="text-slate-700 leading-relaxed text-[14px] text-justify">
            This section covers the step-by-step chemical synthesis of all the specific drugs mentioned with a superscript (*) in the BP601T (Medicinal Chemistry III) syllabus. Understanding these reaction pathways, intermediate formations, and reagents is crucial for university examinations.
        </p>
    </div>

    <!-- ANTIBIOTICS -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm">
        <h3 class="text-[18px] font-black text-indigo-800 mb-4 border-b border-slate-100 pb-2">1. Antibiotics</h3>
        
        <div class="space-y-4">
            <div class="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 class="font-bold text-indigo-900 text-[15px] mb-2">A. Chloramphenicol</h4>
                <p class="text-[13px] text-slate-700 font-mono leading-loose">
                    p-Nitroacetophenone <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Br<sub>2</sub> / HBr)</span> <br>
                    p-Nitrophenacyl bromide <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Hexamine, EtOH / HCl)</span> <br>
                    p-Nitrophenacyl amine <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Dichloroacetyl chloride / Acetic anhydride)</span> <br>
                    Intermediate Amide <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(HCHO / Na<sub>2</sub>CO<sub>3</sub>)</span> <br>
                    Hydroxymethyl derivative <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Aluminium isopropoxide / MPV Reduction)</span> <br>
                    <strong>Chloramphenicol</strong>
                </p>
            </div>
        </div>
    </div>

    <!-- ANTIMALARIALS -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm">
        <h3 class="text-[18px] font-black text-indigo-800 mb-4 border-b border-slate-100 pb-2">2. Antimalarials</h3>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="bg-emerald-50 p-4 rounded-lg border border-emerald-100">
                <h4 class="font-bold text-emerald-900 text-[15px] mb-2">A. Chloroquine</h4>
                <p class="text-[13px] text-slate-700 font-mono leading-loose">
                    4,7-Dichloroquinoline <br>
                    &nbsp;&nbsp;+ <br>
                    4-Amino-1-diethylaminopentane <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Phenol, Heat 180&deg;C, Base)</span> <br>
                    <strong>Chloroquine</strong> + HCl
                </p>
            </div>
            <div class="bg-emerald-50 p-4 rounded-lg border border-emerald-100">
                <h4 class="font-bold text-emerald-900 text-[15px] mb-2">B. Pamaquine</h4>
                <p class="text-[13px] text-slate-700 font-mono leading-loose">
                    4-Methoxy-2-nitroaniline + Glycerol <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Skraup Synthesis / Conc. H<sub>2</sub>SO<sub>4</sub>)</span> <br>
                    6-Methoxy-8-nitroquinoline <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Reduction: Fe / HCl)</span> <br>
                    6-Methoxy-8-aminoquinoline <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(+ 4-Bromo-1-diethylaminopentane / K<sub>2</sub>CO<sub>3</sub>)</span> <br>
                    <strong>Pamaquine</strong>
                </p>
            </div>
        </div>
    </div>

    <!-- ANTI-TUBERCULAR & UTI -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm">
        <h3 class="text-[18px] font-black text-indigo-800 mb-4 border-b border-slate-100 pb-2">3. Anti-tubercular & UTI Agents</h3>
        
        <div class="space-y-4">
            <div class="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <h4 class="font-bold text-blue-900 text-[15px] mb-2">A. Isoniazid (INH)</h4>
                <p class="text-[13px] text-slate-700 font-mono leading-loose">
                    4-Methylpyridine (gamma-picoline) <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Oxidation: KMnO<sub>4</sub>)</span> <br>
                    Isonicotinic Acid <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(C<sub>2</sub>H<sub>5</sub>OH / H<sub>2</sub>SO<sub>4</sub>)</span> <br>
                    Ethyl isonicotinate <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(NH<sub>2</sub>NH<sub>2</sub> - Hydrazine)</span> <br>
                    <strong>Isoniazid</strong>
                </p>
            </div>

            <div class="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <h4 class="font-bold text-blue-900 text-[15px] mb-2">B. Para Amino Salicylic Acid (PAS)</h4>
                <p class="text-[13px] text-slate-700 font-mono leading-loose">
                    Anthranilic Acid (2-Aminobenzoic acid) <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(HNO<sub>3</sub> / H<sub>2</sub>SO<sub>4</sub>)</span> <br>
                    4-Nitroanthranilic Acid <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(NaNO<sub>2</sub> / HCl, 0-5&deg;C)</span> <br>
                    Diazonium Salt <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(H<sub>2</sub>O, Heat)</span> <br>
                    4-Nitro-2-hydroxybenzoic Acid <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Reduction: Fe / HCl)</span> <br>
                    <strong>Para Amino Salicylic Acid</strong>
                </p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="bg-cyan-50 p-4 rounded-lg border border-cyan-100">
                    <h4 class="font-bold text-cyan-900 text-[15px] mb-2">C. Ciprofloxacin</h4>
                    <p class="text-[13px] text-slate-700 font-mono leading-loose">
                        2,4-Dichloro-5-fluorobenzoyl chloride <br>
                        &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Ethyl malonate / Mg)</span> <br>
                        Intermediate <br>
                        &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Triethyl orthoformate / Acetic anhydride)</span> <br>
                        Intermediate <br>
                        &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Cyclopropylamine)</span> <br>
                        Enamine Intermediate <br>
                        &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Base / Heat)</span> <br>
                        7-Chloro-1-cyclopropyl-6-fluoro-1,4-dihydro-4-oxoquinoline-3-carboxylic acid <br>
                        &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Piperazine)</span> <br>
                        <strong>Ciprofloxacin</strong>
                    </p>
                </div>
                <div class="bg-cyan-50 p-4 rounded-lg border border-cyan-100">
                    <h4 class="font-bold text-cyan-900 text-[15px] mb-2">D. Nitrofurantoin</h4>
                    <p class="text-[13px] text-slate-700 font-mono leading-loose">
                        Chloroacetic acid + KCNO <br>
                        &nbsp;&nbsp;&darr; <br>
                        Hydantoin <br>
                        &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Hydrazine, NH<sub>2</sub>NH<sub>2</sub>)</span> <br>
                        1-Aminohydantoin <br>
                        &nbsp;&nbsp;+ <br>
                        5-Nitrofurfural diacetate <br>
                        &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Aq. Acid / HCl, pH 3-4)</span> <br>
                        <strong>Nitrofurantoin</strong>
                    </p>
                </div>
            </div>
        </div>
    </div>

    <!-- ANTIVIRAL & ANTIFUNGAL -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm">
        <h3 class="text-[18px] font-black text-indigo-800 mb-4 border-b border-slate-100 pb-2">4. Antiviral & Antifungal Agents</h3>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="bg-purple-50 p-4 rounded-lg border border-purple-100">
                <h4 class="font-bold text-purple-900 text-[15px] mb-2">A. Acyclovir</h4>
                <p class="text-[13px] text-slate-700 font-mono leading-loose">
                    Guanine <br>
                    &nbsp;&nbsp;+ <br>
                    2-(Benzoyloxymethoxy)ethyl chloride <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Triethylamine / HMDS)</span> <br>
                    Intermediate <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Hydrolysis: NaOH / Heat)</span> <br>
                    <strong>Acyclovir</strong> + Sodium benzoate
                </p>
            </div>
            
            <div class="bg-pink-50 p-4 rounded-lg border border-pink-100">
                <h4 class="font-bold text-pink-900 text-[15px] mb-2">B. Miconazole</h4>
                <p class="text-[13px] text-slate-700 font-mono leading-loose">
                    2,4-Dichlorophenacyl bromide <br>
                    &nbsp;&nbsp;+ <span class="text-[11px] text-slate-500 font-sans">(Imidazole)</span> <br>
                    1-(2,4-Dichlorobenzoylmethyl)-imidazole <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Reduction: NaBH<sub>4</sub>)</span> <br>
                    1-(2,4-Dichlorophenyl)-2-(1-imidazolyl)-ethanol <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(2,4-Dichlorobenzyl chloride / NaH)</span> <br>
                    <strong>Miconazole</strong>
                </p>
            </div>

            <div class="bg-pink-50 p-4 rounded-lg border border-pink-100 md:col-span-2">
                <h4 class="font-bold text-pink-900 text-[15px] mb-2">C. Tolnaftate</h4>
                <p class="text-[13px] text-slate-700 font-mono leading-loose">
                    2-Naphthol + Thiophosgene (CSCl<sub>2</sub>) <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(-HCl)</span> <br>
                    2-Naphthyl chlorothioformate <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(+ N-Methyl-m-toluidine)</span> <br>
                    <strong>Tolnaftate</strong>
                </p>
            </div>
        </div>
    </div>

    <!-- ANTI-PROTOZOAL & ANTHELMINTICS -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm">
        <h3 class="text-[18px] font-black text-indigo-800 mb-4 border-b border-slate-100 pb-2">5. Anti-protozoal & Anthelmintics</h3>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="bg-orange-50 p-4 rounded-lg border border-orange-100">
                <h4 class="font-bold text-orange-900 text-[15px] mb-2">A. Metronidazole</h4>
                <p class="text-[13px] text-slate-700 font-mono leading-loose">
                    Glyoxal + Acetaldehyde + 2NH<sub>3</sub> <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Cyclization)</span> <br>
                    2-Methylimidazole <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(HNO<sub>3</sub> / H<sub>2</sub>SO<sub>4</sub>)</span> <br>
                    2-Methyl-5-nitroimidazole <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Ethylene chlorohydrin / NaOH)</span> <br>
                    <strong>Metronidazole</strong>
                </p>
            </div>
            
            <div class="bg-amber-50 p-4 rounded-lg border border-amber-100">
                <h4 class="font-bold text-amber-900 text-[15px] mb-2">B. Diethylcarbamazine</h4>
                <p class="text-[13px] text-slate-700 font-mono leading-loose">
                    Piperazine <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Diethylcarbamoyl chloride)</span> <br>
                    N-Diethylcarbamoyl piperazine <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(HCHO / HCOOH - Methylation)</span> <br>
                    <strong>Diethylcarbamazine (DEC)</strong>
                </p>
            </div>

            <div class="bg-amber-50 p-4 rounded-lg border border-amber-100">
                <h4 class="font-bold text-amber-900 text-[15px] mb-2">C. Mebendazole</h4>
                <p class="text-[13px] text-slate-700 font-mono leading-loose">
                    4-Chlorobenzophenone <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(HNO<sub>3</sub>)</span> <br>
                    4-Chloro-3-nitrobenzophenone <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(NH<sub>3</sub>)</span> <br>
                    4-Amino-3-nitrobenzophenone <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Reduction: H<sub>2</sub> / Pd-C)</span> <br>
                    3,4-Diaminobenzophenone <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(S-Methyl isothiourea + Methyl chloroformate)</span> <br>
                    <strong>Mebendazole</strong>
                </p>
            </div>
        </div>
    </div>

    <!-- SULFONAMIDES -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm">
        <h3 class="text-[18px] font-black text-indigo-800 mb-4 border-b border-slate-100 pb-2">6. Sulfonamides & Sulfones</h3>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div class="bg-rose-50 p-4 rounded-lg border border-rose-100 lg:col-span-2">
                <h4 class="font-bold text-rose-900 text-[15px] mb-2">A. Sulfacetamide</h4>
                <p class="text-[13px] text-slate-700 font-mono leading-loose">
                    Sulfanilamide <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Acetic anhydride / Acetylation)</span> <br>
                    N<sup>1</sup>,N<sup>4</sup>-Diacetylsulfanilamide <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Selective Alkaline Hydrolysis)</span> <br>
                    <strong>Sulfacetamide</strong>
                </p>
            </div>
            
            <div class="bg-rose-50 p-4 rounded-lg border border-rose-100 lg:col-span-2">
                <h4 class="font-bold text-rose-900 text-[15px] mb-2">B. Sulfamethoxazole</h4>
                <p class="text-[13px] text-slate-700 font-mono leading-loose">
                    p-Acetamidobenzenesulfonyl chloride <br>
                    &nbsp;&nbsp;+ <span class="text-[11px] text-slate-500 font-sans">(3-Amino-5-methylisoxazole)</span> <br>
                    Condensation Product <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Alkaline Hydrolysis)</span> <br>
                    <strong>Sulfamethoxazole</strong>
                </p>
            </div>

            <div class="bg-red-50 p-4 rounded-lg border border-red-100 lg:col-span-2">
                <h4 class="font-bold text-red-900 text-[15px] mb-2">C. Trimethoprim</h4>
                <p class="text-[13px] text-slate-700 font-mono leading-loose">
                    3,4,5-Trimethoxybenzaldehyde <br>
                    &nbsp;&nbsp;+ <span class="text-[11px] text-slate-500 font-sans">(3-Ethoxypropionitrile / Base)</span> <br>
                    Intermediate <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Guanidine)</span> <br>
                    <strong>Trimethoprim</strong>
                </p>
            </div>

            <div class="bg-red-50 p-4 rounded-lg border border-red-100 lg:col-span-2">
                <h4 class="font-bold text-red-900 text-[15px] mb-2">D. Dapsone</h4>
                <p class="text-[13px] text-slate-700 font-mono leading-loose">
                    p-Chloronitrobenzene + Na<sub>2</sub>S <br>
                    &nbsp;&nbsp;&darr; <br>
                    Bis(4-nitrophenyl) sulfide <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Oxidation: Chromic acid / [O])</span> <br>
                    Bis(4-nitrophenyl) sulfone <br>
                    &nbsp;&nbsp;&darr; <span class="text-[11px] text-slate-500 font-sans">(Reduction: Sn / HCl)</span> <br>
                    <strong>Dapsone</strong>
                </p>
            </div>
        </div>
    </div>

</div>
`;

export default BP601TSynthesisNotes;